import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { getPathLabel, postTrackingEvents } from "./pageTrackingUtils";

const TRACK_ENDPOINT = "/api/v1/user_activity/track_auth_user";

// Human-readable page names from client-portal URL paths.
// Order matters — more specific prefixes must come before broader ones.
const PAGE_LABELS = [
  // ── Dashboard ──────────────────────────────────────────────
  ["/dashboard", "Client Dashboard"],

  // ── Net Worth / Budgets ───────────────────────────────────
  ["/net-worth", "Net Worth"],
  ["/spending/category", "Spending Category"],
  ["/spending", "Spending"],
  ["/transactions", "Transactions"],
  ["/budgets", "Budgets"],

  // ── Vault / Docs ──────────────────────────────────────────
  ["/vault", "Vault"],

  // ── Financial Story ───────────────────────────────────────
  ["/financial-story", "Financial Story"],

  // ── Planning ──────────────────────────────────────────────
  ["/planning", "Planning"],

  // ── YouVest ───────────────────────────────────────────────
  ["/youvest/cover-page", "YouVest Cover Page"],
  ["/youvest/activity", "YouVest Activity"],
  ["/youvest/tutorials", "YouVest Tutorials"],
  ["/youvest", "YouVest"],

  // ── Learning Lab ──────────────────────────────────────────
  ["/learning_lab", "Learning Lab"],

  // ── Risk Tolerance ────────────────────────────────────────
  ["/risk-tolerance", "Risk Tolerance"],

  // ── Values ────────────────────────────────────────────────
  ["/values/exercise", "Values Exercise"],

  // ── Misc client pages ─────────────────────────────────────
  ["/partners", "Partners"],
  ["/resources", "Resources"],
  ["/abundo-love-corner", "Abundo Love Corner"],
  ["/notes", "Notes"],
  ["/advice-library", "Advice Library"],
  ["/documentation", "Documentation"],
  ["/settings", "Settings"],
];

function getPageLabel(pathname) {
  return getPathLabel(pathname, PAGE_LABELS, pathname);
}

// Household ID is not present in client-portal URLs; backend infers from the token.
function extractHouseholdId(pathname) {
  const match = pathname.match(/\/households\/([0-9a-f-]{36})/i);
  return match ? match[1] : null;
}

// Advisor portal paths — skip these so the advisor hook owns them even if a
// user briefly has both roles / is impersonating.
function isAdvisorPath(pathname) {
  return pathname.startsWith("/crm/") || pathname.startsWith("/content/");
}

/**
 * Tracks client-portal page views and time-on-page.
 * Mirrors useAdvisorPageTracking but posts to /api/v1/user_activity/track_auth_user.
 * No-op for advisors and unauthenticated visitors.
 */
export function useClientPageTracking() {
  const location = useLocation();
  const { isAdvisor, accessToken } = useAuth();
  const active = !isAdvisor && !!accessToken;

  const queueRef = useRef([]);
  const pageEnteredRef = useRef(null);
  const lastPathRef = useRef(null);
  const flushTimerRef = useRef(null);

  const flush = useCallback(() => {
    if (queueRef.current.length === 0) return;

    const events = [...queueRef.current];
    queueRef.current = [];

    postTrackingEvents(TRACK_ENDPOINT, events);
  }, []);

  const closePage = useCallback(() => {
    if (!lastPathRef.current || !pageEnteredRef.current) return;

    const durationMs = Date.now() - pageEnteredRef.current;
    if (durationMs < 1000) return;

    queueRef.current.push({
      type: "Page View",
      details: getPageLabel(lastPathRef.current),
      household_id: extractHouseholdId(lastPathRef.current),
      resource_type: "page",
      resource_id: lastPathRef.current,
      metadata: {
        path: lastPathRef.current,
        duration_seconds: Math.round(durationMs / 1000),
      },
    });
  }, []);

  const trackTab = useCallback(
    (tabName, extra = {}) => {
      if (!active) return;

      queueRef.current.push({
        type: "Tab View",
        details: tabName,
        household_id:
          extra.householdId || extractHouseholdId(lastPathRef.current || ""),
        resource_type: "tab",
        resource_id: tabName,
        metadata: {
          path: lastPathRef.current,
          tab: tabName,
          ...extra,
        },
      });
    },
    [active]
  );

  useEffect(() => {
    if (!active) return;

    const newPath = location.pathname;
    if (isAdvisorPath(newPath)) return;
    if (newPath === lastPathRef.current) return;

    closePage();

    lastPathRef.current = newPath;
    pageEnteredRef.current = Date.now();
  }, [location.pathname, active, closePage]);

  useEffect(() => {
    if (!active) return;

    flushTimerRef.current = setInterval(flush, 30_000);
    return () => clearInterval(flushTimerRef.current);
  }, [active, flush]);

  useEffect(() => {
    if (!active) return;

    const handleUnload = () => {
      closePage();

      if (queueRef.current.length === 0) return;

      // sendBeacon can't set Authorization headers, and the server's auth
      // middleware only reads the Authorization header — not query params.
      // Use keepalive fetch so the request carries proper auth on unload.
      postTrackingEvents(TRACK_ENDPOINT, queueRef.current, { keepalive: true });

      queueRef.current = [];
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [active, closePage]);

  return { trackTab };
}
